import { RpcError, StatusCode } from "grpc-web";
import { MessageServicePromiseClient } from "@/pb/scout/message/v1/message_grpc_web_pb";
import {
  ReserveScoutMessageRequest,
  ReserveScoutMessageForAgentRequest,
  SendScoutMessageRequest,
  SendScoutMessageForAgentRequest,
  ThreadFilter,
  SearchThreadsRequest,
  SearchAgentThreadsRequest,
  GetThreadCommentsRequest,
  GetThreadUserRequest,
  SendThreadCommentRequest,
  ReadCommentRequest,
  ReserveThreadCommentRequest,
  DeleteReservedCommentRequest,
  BulkSendScoutMessageRequest,
  BulkSendScoutMessageForAgentRequest
} from "@/pb/scout/message/v1/message_pb";
import { handleGrpcError } from "@/grpc-web/utils";

const client: MessageServicePromiseClient = Object.assign(
  new MessageServicePromiseClient(process.env.VUE_APP_SCOUT_GRPC_ORIGIN, null, {
    withCredentials: true
  })
);

export const sendScoutMessage = async (
  toUserId: number,
  text: string,
  messageTemplateId: number | undefined
) => {
  const request = new SendScoutMessageRequest();
  request.setToUserId(toUserId);
  request.setText(text);
  if (messageTemplateId !== undefined) {
    request.setMessageTemplateId(messageTemplateId);
  }
  const _ = await client.sendScoutMessage(request).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });

  return null;
};

export const sendScoutMessageForAgent = async (
  toUserId: number,
  text: string,
  messageTemplateId: number | undefined
) => {
  const request = new SendScoutMessageForAgentRequest();
  request.setToUserId(toUserId);
  request.setText(text);
  if (messageTemplateId !== undefined) {
    request.setMessageTemplateId(messageTemplateId);
  }

  const _ = await client
    .sendScoutMessageForAgent(request)
    .catch((e: RpcError) => {
      handleGrpcError(e);
      throw e;
    });

  return null;
};

export const bulkSendScoutMessage = async (
  toUserIds: number[],
  text: string,
  messageTemplateId: number | undefined
) => {
  const request = new BulkSendScoutMessageRequest();
  request.setToUserIdsList(toUserIds);
  request.setText(text);
  if (messageTemplateId !== undefined) {
    request.setMessageTemplateId(messageTemplateId);
  }
  const response = await client
    .bulkSendScoutMessage(request)
    .catch((e: RpcError) => {
      handleGrpcError(e);
      throw e;
    });

  return response.getExcludedScoutCandidateCount();
};

export const bulkSendScoutMessageForAgent = async (
  toUserIds: number[],
  text: string,
  messageTemplateId: number | undefined
) => {
  const request = new BulkSendScoutMessageForAgentRequest();
  request.setToUserIdsList(toUserIds);
  request.setText(text);
  if (messageTemplateId !== undefined) {
    request.setMessageTemplateId(messageTemplateId);
  }
  const response = await client
    .bulkSendScoutMessageForAgent(request)
    .catch((e: RpcError) => {
      handleGrpcError(e);
      throw e;
    });

  return response.getExcludedScoutCandidateCount();
};

export const reserveScoutMessage = async (
  toUserId: number,
  text: string,
  scheduledAtMs: number,
  messageTemplateId: number | undefined
) => {
  const request = new ReserveScoutMessageRequest();
  request.setToUserId(toUserId);
  request.setText(text);
  request.setScheduledSendAtMs(scheduledAtMs);
  // メッセージテンプレートが選択されている場合はメッセージテンプレートIDを設定する
  if (messageTemplateId !== undefined) {
    request.setMessageTemplateId(messageTemplateId);
  }

  const _ = await client.reserveScoutMessage(request).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });

  return null;
};

export const reserveScoutMessageForAgent = async (
  toUserId: number,
  text: string,
  scheduledAtMs: number,
  messageTemplateId: number | undefined
) => {
  const request = new ReserveScoutMessageForAgentRequest();
  request.setToUserId(toUserId);
  request.setText(text);
  request.setScheduledSendAtMs(scheduledAtMs);
  // メッセージテンプレートが選択されている場合はメッセージテンプレートIDを設定する
  if (messageTemplateId !== undefined) {
    request.setMessageTemplateId(messageTemplateId);
  }

  const _ = await client
    .reserveScoutMessageForAgent(request)
    .catch((e: RpcError) => {
      handleGrpcError(e);
      throw e;
    });

  return null;
};

export const sendThreadComment = async (threadId: number, text: string) => {
  const request = new SendThreadCommentRequest();
  request.setThreadId(threadId);
  request.setText(text);
  const _ = await client.sendThreadComment(request).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });

  return null;
};

export const reserveThreadComment = async (
  threadId: number,
  text: string,
  scheduledAtMs: number
) => {
  const request = new ReserveThreadCommentRequest();
  request.setThreadId(threadId);
  request.setText(text);
  request.setScheduledSendAtMs(scheduledAtMs);
  const _ = await client.reserveThreadComment(request).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });

  return null;
};

export const deleteReservedComment = async (commentId: number) => {
  const request = new DeleteReservedCommentRequest();
  request.setCommentId(commentId);
  const _ = await client.deleteReservedComment(request).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });

  return null;
};

export const searchMessageThreads = async (
  filter: ThreadFilter,
  nameOrUniqueId: string,
  unansweredOnly: boolean,
  page: number,
  per: number
) => {
  const request = new SearchThreadsRequest();
  request.setFilter(filter);
  if (nameOrUniqueId.length > 0) {
    request.setKeywordForNameOrUniqueId(nameOrUniqueId);
  }
  request.setOnlyNotResponded(unansweredOnly);
  request.setPage(page);
  request.setPer(per);

  const response = await client.searchThreads(request).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });

  return response.getThreadsList();
};

export const searchAgentMessageThreads = async (
  filter: ThreadFilter,
  nameOrUniqueId: string,
  unansweredOnly: boolean,
  page: number,
  per: number
) => {
  const request = new SearchAgentThreadsRequest();
  request.setFilter(filter);
  if (nameOrUniqueId.length > 0) {
    request.setKeywordForNameOrUniqueId(nameOrUniqueId);
  }
  request.setOnlyNotResponded(unansweredOnly);
  request.setPage(page);
  request.setPer(per);

  const response = await client
    .searchAgentThreads(request)
    .catch((e: RpcError) => {
      handleGrpcError(e);
      throw e;
    });

  return response.getThreadsList();
};

export const getThreadComments = async (
  threadId: number,
  page: number,
  per: number
) => {
  const request = new GetThreadCommentsRequest();
  request.setThreadId(threadId);
  request.setPage(page);
  request.setPer(per);

  return await client.getThreadComments(request).catch((e: RpcError) => {
    // スレッドコメントが0件の場合にコンポーネント側でエラーにしたくないので404ページには遷移させずundefinedを返している
    if (e.code === StatusCode.NOT_FOUND) {
      return undefined;
    }

    handleGrpcError(e);
    throw e;
  });
};

export const getThreadUser = async (threadId: number) => {
  const request = new GetThreadUserRequest();
  request.setThreadId(threadId);

  const response = await client.getThreadUser(request).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });

  return response.getUser();
};

export const readComment = async (commentId: number) => {
  const request = new ReadCommentRequest();
  request.setCommentId(commentId);

  const _ = await client.readComment(request).catch((e: RpcError) => {
    handleGrpcError(e);
    throw e;
  });
};
